/**
 * Availability API service
 * Handles all API calls related to astrologer availability
 * Uses centralized axios instance with interceptors from api.js
 */

import api from './api';

/**
 * Set availability for a specific date
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Array} slots - Array of slot objects with period and slot_indices
 * @returns {Promise} API response
 */
export const setAvailability = async (date, slots) => {
  console.log(`[API] Setting availability for ${date}`, slots);

  try {
    const response = await api.put('/astrologers/availability',
      { slots },
      { params: { date } }
    );

    console.log('[API] Availability saved:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Error setting availability:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get availability for astrologer on specific date
 * @param {number} astrologerId - Astrologer ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise} API response with available slots
 */
export const getAvailability = async (astrologerId, date) => {
  console.log(`[API] Fetching availability for astrologer ${astrologerId} on ${date}`);

  try {
    const response = await api.get(
      `/astrologers/${astrologerId}/availability`,
      { params: { date } }
    );

    console.log(`[API] Found ${response.data.data?.total_available || 0} available slots`);
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching availability:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get all available dates for astrologer within date range
 * @param {number} astrologerId - Astrologer ID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise} API response with available dates
 */
export const getAvailableDates = async (astrologerId, startDate, endDate) => {
  console.log(`[API] Fetching available dates for astrologer ${astrologerId} from ${startDate} to ${endDate}`);

  try {
    const response = await api.get(
      `/astrologers/${astrologerId}/available-dates`,
      {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      }
    );

    console.log(`[API] Found ${response.data.data?.total_available_dates || 0} available dates`);
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching available dates:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get list of all astrologers
 * @returns {Promise} API response with astrologer list
 */
export const getAstrologers = async () => {
  console.log('[API] Fetching astrologers list');

  try {
    const response = await api.get('/astrologers');
    console.log(`[API] Found ${response.data.length || 0} astrologers`);
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching astrologers:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete availability for a specific slot
 * @param {number} slotId - Slot ID to delete
 * @returns {Promise} API response
 */
export const deleteAvailabilitySlot = async (slotId) => {
  console.log(`[API] Deleting availability slot ${slotId}`);

  try {
    const response = await api.delete(`/astrologers/availability/${slotId}`);
    console.log('[API] Slot deleted successfully');
    return response.data;
  } catch (error) {
    console.error('[API] Error deleting slot:', error.response?.data || error.message);
    throw error;
  }
};
